
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface FeatureLimit {
  limit: string[] | number;
  description?: string;
}

interface CharacterFeatures {
  browse: boolean;
  make_public: boolean;
  description?: string;
}

export const useFeatureLimits = () => {
  const { data: featureLimits, isLoading } = useQuery({
    queryKey: ['featureLimits'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // Get user's active subscription tier
      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('tier')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .single();

      if (!subscription) return null;

      // Get feature limits for the user's tier
      const { data: limits } = await supabase
        .from('subscription_feature_limits')
        .select('*')
        .eq('tier', subscription.tier);

        //logger.log("subscription",subscription);
      //logger.log("limits",limits);

      // Transform the data into a more usable format
      const formattedLimits = limits?.reduce((acc, limit) => {
        // Parse the JSON string into an object
        const value = typeof limit.feature_value === 'string' 
          ? JSON.parse(limit.feature_value) 
          : limit.feature_value;
          
        acc[limit.feature_key] = value;
        return acc;
      }, {} as Record<string, FeatureLimit | CharacterFeatures>);

      //logger.log("formattedLimits",formattedLimits);

      return formattedLimits;
    }
  });

  const isFeatureEnabled = (featureKey: string, value: string) => {
    if (!featureLimits || isLoading) return false;
    
    const limit = featureLimits[featureKey];
    if (!limit) return false;

    // Handle unlimited access
    if ('limit' in limit && limit.limit === -1) return true;

    // Handle array of allowed values
    if ('limit' in limit && Array.isArray(limit.limit)) {
      return limit.limit.includes(value);
    }
    
    // Handle array of allowed values
    if (Array.isArray(limit)) {
      return limit.includes(value);
    }

    //check if value is a property of limit
    if (value in limit) {
      return limit[value];
    }

    return false;
  };

  const getFeatureDescription = (featureKey: string) => {
    if (!featureLimits || isLoading) return '';
    const limit = featureLimits[featureKey];
    return limit && 'description' in limit ? limit.description : '';
  };

  const getCharacterFeatures = () => {
    if (!featureLimits || isLoading) return { browse: false, make_public: false };
    const features = featureLimits['character_features'] as CharacterFeatures;
    return features || { browse: false, make_public: false };
  };

  return {
    isFeatureEnabled,
    getFeatureDescription,
    getCharacterFeatures,
    isLoading
  };
};
